import { Anchor, Badge, Box, Button, Card, Flex, Grid, Tooltip } from '@mantine/core';
import {
  IconAlertTriangle,
  IconBell,
  IconBolt,
  IconBrandFacebook,
  IconBrandGoogle,
  IconBrandWhatsapp,
  IconCalendar,
  IconChartLine,
  IconClockHour4,
  IconDeviceMobile,
  IconHistory,
  IconMail,
  IconPhone,
  IconSettings,
  IconUsers,
  IconWebhook,
} from '@tabler/icons-react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import prettifiedLabel from '../../constants/labelPrettifier';
import AdSetStatusVisualizer from '../AdSetStatusVisualizer';
import GoogleStatusVisualizer from '../Google/GoogleStatusVisualizer';
import CampaignsStats from './CampaignsStats';

// Map message types by data.custom.key to icons
const messageTypeIcons = {
  sendEmail: { icon: IconMail, label: 'Email' },
  sendViberMessage: { icon: IconPhone, label: 'Viber' },
  sendTextMessage: { icon: IconDeviceMobile, label: 'SMS' },
  sendPushNotification: { icon: IconBell, label: 'Push' },
  sendFacebookAd: { icon: IconBrandFacebook, label: 'Facebook' },
  sendGoogleAd: { icon: IconBrandGoogle, label: 'Google' },
  sendWhatsappMessage: { icon: IconBrandWhatsapp, label: 'WhatsApp' },
  sendWebhook: { icon: IconWebhook, label: 'Webhook' },
};

const CampaignsCard = ({ campaign, status, stats, period, facebookProgress, googleProgress }) => {
  const currentDomain = useSelector((state) => state.domain.domain);
  const domainCurrency = currentDomain.currency;
  const { accountId, domainId } = useParams();
  const location = useLocation();
  const linkToSegment = campaign.segment
    ? `#/accounts/${accountId}/domains/${domainId}/segments/${campaign.segment.id}`
    : null;

  const messageTypesSet = new Set();

  campaign.flow.nodes.forEach((node) => {
    const key = node.data?.custom?.key;
    if (key && messageTypeIcons[key]) {
      messageTypesSet.add(key);
    }
  });

  const messageTypesArray = Array.from(messageTypesSet);

  return (
    <Card mb="md" withBorder data-tour="campaignCard">
      <Card.Section inheritPadding py="sm">
        <Grid>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Box mb="xs">
              <Flex align="center" gap="xs" style={{ display: 'inline-flex' }}>
                <Anchor
                  href={`#${location.pathname}/${campaign.id}/details?mode=${campaign.mode}`}
                  fw={600}
                  style={{ display: 'inline' }}
                >
                  {campaign.name}
                </Anchor>
                {facebookProgress &&
                  campaign.flow.nodes.some((node) => node.data?.custom?.key === 'sendFacebookAd') && (
                    <Box style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <AdSetStatusVisualizer adSetStatuses={facebookProgress} />
                    </Box>
                  )}
                {googleProgress && campaign.flow.nodes.some((node) => node.data?.custom?.key === 'sendGoogleAd') && (
                  <Box style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <GoogleStatusVisualizer statuses={googleProgress} type="google" />
                  </Box>
                )}
              </Flex>
            </Box>
            <Flex gap="xs" wrap="wrap">
              {campaign.segment && (
                <Badge variant="default">
                  <IconUsers size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  <Anchor href={linkToSegment} target="_blank" size="xs">
                    {campaign.segment?.name || 'Unknown'}
                  </Anchor>
                </Badge>
              )}
              {campaign.mode === 'triggered' && (
                <Badge variant="default">
                  {campaign.trigger.type === 'event' && (
                    <>
                      <IconBolt size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      {campaign.trigger.actions?.map((a) => prettifiedLabel(a))}
                    </>
                  )}
                  {campaign.trigger.type === 'webhook' && (
                    <>
                      <IconSettings size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      Webhook
                    </>
                  )}
                </Badge>
              )}
              {messageTypesArray.length > 0 && (
                <>
                  {messageTypesArray.map((key, index) => {
                    const typeConfig = messageTypeIcons[key];
                    const IconComponent = typeConfig.icon;
                    return (
                      <Tooltip key={index} label={typeConfig.label}>
                        <Badge variant="default">
                          <IconComponent size={14} style={{ verticalAlign: 'middle' }} />
                        </Badge>
                      </Tooltip>
                    );
                  })}
                </>
              )}
              {campaign.useOptimalTime && (
                <Badge variant="default">
                  <IconClockHour4 size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Optimal Time
                </Badge>
              )}
              {campaign.scope === 'transactional' && (
                <Badge variant="default">
                  <IconAlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Transactional
                </Badge>
              )}
              {campaign.scheduledAt && (
                <Badge variant="default">
                  <IconCalendar size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  {campaign.mode === 'continuous' ? 'Starts: ' : ''}
                  {new Date(campaign.scheduledAt).toLocaleString()}
                </Badge>
              )}
              {campaign.endsAt && new Date(campaign.endsAt) < new Date() && (
                <Badge variant="default">
                  <IconCalendar size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Ended: {new Date(campaign.endsAt).toLocaleString()}
                </Badge>
              )}
              <Tooltip
                label={
                  <>
                    <div>Created: {new Date(campaign.createdAt).toLocaleString()}</div>
                    <div>Updated: {new Date(campaign.updatedAt).toLocaleString()}</div>
                  </>
                }
                position="top"
                withArrow
              >
                <Badge variant="default" style={{ cursor: 'pointer' }}>
                  <IconHistory size={14} style={{ verticalAlign: 'middle' }} />
                </Badge>
              </Tooltip>
            </Flex>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Flex justify="flex-end" align="flex-start" gap="sm">
              <Button
                component="a"
                href={`#${location.pathname}/${campaign.id}/analytics?mode=${campaign.mode}`}
                size="xs"
                rightSection={<IconChartLine size={14} />}
              >
                Statistics
              </Button>
              <Box data-tour="campaignStatus">{status}</Box>
            </Flex>
          </Grid.Col>
        </Grid>
      </Card.Section>
      <Card.Section inheritPadding py="sm" ta="left">
        <Grid>
          <Grid.Col span={12}>
            <CampaignsStats stats={stats} campaign={campaign} period={period} currency={domainCurrency} />
          </Grid.Col>
        </Grid>
      </Card.Section>
    </Card>
  );
};

export default CampaignsCard;
